// Global Types
import type {
  $ExtraColumnData,
  $FullRegattaResultTable,
  $FullRegattaResultTableEntry,
  $FullRegattaResultTableRace,
  $Model,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import {
  i18next,
} from 'common.language';
import {
  getDocumentUrl,
  getExtraColumnData,
  getExtraColumnValue,
  getRegattaEntrySubTitle,
  getRegattaEntryTitle,
  getResultPlaceString,
  getResultTimeString,
  getTimeDiff,
  shortenText,
  sortResults,
} from 'common.helpers/dist/browser';
import config from '../../../config';

// Components
import RaceInfoButton from '../RaceInfoButton';

// Types
type $RaceView = 'ct' | 'et' | 'finish' | 'speed';

type $Props = $Translate & {
  item: $FullRegattaResultTable;
  regatta: $Model<'Regatta'>;
};

type $State = {
  raceViews: Record<string, $RaceView>;
  showAll: boolean;
};

const viewText: Record<$RaceView, string> = {
  ct: 'CT',
  et: 'ET',
  finish: 'Finish',
  speed: '~V kts',
};

const getNextView = (
  race: $FullRegattaResultTableRace,
): {
  ct: $RaceView;
  et: $RaceView;
  finish: $RaceView;
  speed?: $RaceView;
} => (race.Distance ? {
  ct: 'speed',
  et: 'ct',
  finish: 'et',
  speed: 'finish',
} : {
  ct: 'finish',
  et: 'ct',
  finish: 'et',
});

class RegattaResultTableView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      raceViews: {
      },
      showAll: false,
    };
  }

  showAllClass(): string {
    const {
      showAll,
    } = this.state;

    return showAll === true ? '' : 'hidden sm:table-cell';
  }

  renderHead({
    extraColumnData,
    publicRaces,
  }: {
    extraColumnData: $ExtraColumnData;
    publicRaces: Array<$FullRegattaResultTableRace>;
  }) {
    const {
      item,
      t,
    } = this.props;
    const {
      raceViews,
      showAll,
    } = this.state;

    return (
      <thead>
        <tr>
          <th className={'text-left text-green-600 w-48 truncate'}>
            {shortenText(
              item.Name,
              29,
            )}
          </th>
          <th className={showAll === true ? 'px-5' : 'p-0-i'} />
          {extraColumnData.type !== null && (
            <th className={this.showAllClass()} />
          )}
          {publicRaces.map((race: $FullRegattaResultTableRace, index: number) => (
            <th
              className={`relative w-48 ${this.showAllClass()}`}
              key={race.id}
              title={race.Name}
            >
              {!race.Approved && (
                <em
                  className={'la la-hourglass text-red-600 mr-2'}
                  title={t('Preliminary')}
                />
              )}
              <span className={'uppercase'}>
                {`${t('Race')} #${index + 1}`}
              </span>
              <RaceInfoButton data={race} />
            </th>
          ))}
          <th
            className={'uppercase w-48'}
            colSpan={2}
          >
            <span>
              {t('Places')}
            </span>
            <a
              className={'btn btn_light btn_outlined p-1 ml-2'}
              href={getDocumentUrl({
                cdnUrl: config.CDN_URL,
                docRoute: docRoute.regattaResultTable,
                docType: docType.pdf,
                id: item.id,
                language: i18next.language as $Language,
              })}
              rel={'noreferrer'}
              target={'_blank'}
            >
              <em className={'la la-file-pdf'} />
              <small className={'uppercase'}>
                {t('Pdf')}
              </small>
            </a>
          </th>
        </tr>
        <tr className={'border-top text-gray-600 uppercase'}>
          <td className={'text-left text-xs py-1-i pl-2'}>
            {t('Entries')}
          </td>
          <td className={showAll === true ? 'px-5' : 'p-0-i'} />
          {extraColumnData.type !== null && (
            <td className={`text-center text-xs py-1-i ${this.showAllClass()}`}>
              {t(extraColumnData.name)}
            </td>
          )}
          {publicRaces.map((race: $FullRegattaResultTableRace) => {
            const nextView = getNextView(race);

            const activeRaceView: $RaceView | undefined = raceViews[race.id];

            return (
              <td
                className={`py-1-i ${this.showAllClass()}`}
                key={race.id}
              >
                <div className={'flex flex-row items-center justify-center'}>
                  {race.ShowTimeColumn === true && (
                    <div className={'basis-24 text-center text-xs'}>
                      <button
                        className={'btn btn_light btn_outlined font-normal py-0 px-2 uppercase'}
                        onClick={() => {
                          this.setState({
                            raceViews: {
                              ...raceViews,
                              [race.id]: activeRaceView
                                ? nextView[activeRaceView]
                                : nextView.finish,
                            },
                          });
                        }}
                        type={'button'}
                      >
                        {t(viewText[activeRaceView || 'finish'])}
                      </button>
                    </div>
                  )}
                  {race.ShowPointsColumn === true && (
                    <div className={'basis-12 text-center text-xs'}>
                      {t('Pts.')}
                    </div>
                  )}
                  {race.ShowPlaceColumn === true && (
                    <div className={'basis-12 text-center text-xs'}>
                      {t('Plc.')}
                    </div>
                  )}
                </div>
              </td>
            );
          })}
          <td className={'text-center text-xs py-1-i w-24'}>
            {t('Pts.')}
          </td>
          <td className={'text-center text-xs py-1-i w-24'}>
            {t('Plc.')}
          </td>
        </tr>
      </thead>
    );
  }

  renderBody({
    extraColumnData,
    publicRaces,
  }: {
    extraColumnData: $ExtraColumnData;
    publicRaces: Array<$FullRegattaResultTableRace>;
  }) {
    const {
      item,
      regatta,
    } = this.props;
    const {
      showAll,
    } = this.state;

    const sortedRegattaResultTableEntries: Array<$FullRegattaResultTableEntry> = sortResults(
      item.RegattaResultTableEntries,
    );

    return (
      <tbody>
        {sortedRegattaResultTableEntries.map((
          dataItem: $FullRegattaResultTableEntry,
          index: number,
        ) => (
          <tr key={dataItem.id}>
            <td className={`leading-none py-1-i ${showAll === true ? '' : 'RegattaResultTable-fade-entry'}`}>
              <div className={'text-base'}>
                {getRegattaEntryTitle({
                  bowNoEnabled: regatta.BowNoEnabled,
                  countryEnabled: regatta.CountryEnabled,
                  regattaEntry: dataItem.RegattaEntry,
                })}
              </div>
              <small className={'leading-3 text-gray-600'}>
                {getRegattaEntrySubTitle({
                  countryEnabled: regatta.CountryEnabled,
                  regattaEntry: dataItem.RegattaEntry,
                })}
              </small>
            </td>
            <td className={showAll === true ? 'px-5' : 'p-0-i'}>
              {index === 0 && this.renderToggleButton()}
            </td>
            {extraColumnData.type !== null && (
              <td className={`text-center bg-orange-50 ${this.showAllClass()}`}>
                {getExtraColumnValue({
                  extraColumnData,
                  regattaEntry: dataItem.RegattaEntry,
                })}
              </td>
            )}
            {publicRaces.map((
              race: $FullRegattaResultTableRace,
            ) => this.renderResult(
              dataItem,
              race,
            ))}
            <td className={'text-center font-bold bg-blue-50 w-24 whitespace-nowrap'}>
              {dataItem.Points}
            </td>
            <td className={'text-center text-lg font-bold bg-blue text-white w-24 whitespace-nowrap'}>
              {dataItem.Place}
            </td>
          </tr>
        ))}
      </tbody>
    );
  }

  /* eslint-disable-next-line complexity */
  renderResultTimeComponent({
    race,
    result,
  }: {
    race: $FullRegattaResultTableRace;
    result: $Model<'RegattaResultTableRaceResult'>;
  }) {
    const {
      raceViews,
    } = this.state;

    const views = {
      ct: (
        <div className={'leading-none'}>
          <div>{result.CT}</div>
          <small>
            {getTimeDiff(
              result.CT,
              _.map(
                race.RegattaResultTableRaceResults,
                'CT',
              ),
            )}
          </small>
        </div>
      ),
      et: (
        <div className={'leading-none'}>
          <div>{result.ET}</div>
          <small>
            {getTimeDiff(
              result.ET,
              _.map(
                race.RegattaResultTableRaceResults,
                'ET',
              ),
            )}
          </small>
        </div>
      ),
      finish: (
        <div>
          <div>
            {getResultTimeString({
              race,
              result,
            })}
          </div>
          {(result.Position?.startsWith('+') || result.Position?.startsWith('-')) && (
            <small>
              {result.Position}
            </small>
          )}
        </div>
      ),
      speed: (
        <div>
          {race.Distance && result.ET ? (race.Distance / (result.ET / 3600)).toFixed(2) : '-'}
        </div>
      ),
    };

    let view = views.finish;

    const activeView = raceViews[race.id];

    if (activeView) {
      view = views[activeView];
    }

    return view;
  }

  renderResultColumns({
    race,
    result,
  }: {
    race: $FullRegattaResultTableRace;
    result: $Model<'RegattaResultTableRaceResult'>;
  }) {
    const {
      t,
    } = this.props;

    const place: string = getResultPlaceString({
      result,
    });

    return (
      <>
        {race.ShowTimeColumn === true && (
          <div className={'basis-24 text-center'}>
            {this.renderResultTimeComponent({
              race,
              result,
            })}
          </div>
        )}
        {race.ShowPointsColumn === true && (
          <div
            className={'basis-12 text-center'}
            title={t('Points')}
          >
            {result.Points}
          </div>
        )}
        {race.ShowPlaceColumn === true && (
          <div
            className={'basis-12 text-lg text-center font-bold uppercase'}
            title={t('Place')}
          >
            {place}
          </div>
        )}
      </>
    );
  }

  renderToggleButton() {
    const {
      t,
    } = this.props;
    const {
      showAll,
    } = this.state;

    return (
      <div className={'relative sm:hidden'}>
        <button
          className={'btn btn_light btn_outlined uppercase py-1 -rotate-90 absolute w-48 top-12 bg-white z-10'}
          onClick={() => this.setState({
            showAll: !showAll,
          })}
          style={{
            right: '-5rem',
          }}
          type={'button'}
        >
          <span className={'mx-auto'}>
            {showAll === true ? t('Show less') : t('Show full data')}
          </span>
        </button>
      </div>
    );
  }

  renderResult(
    entry: $FullRegattaResultTableEntry,
    race: $FullRegattaResultTableRace,
  ) {
    const result: $Model<'RegattaResultTableRaceResult'> | undefined = _.find(
      race.RegattaResultTableRaceResults,
      {
        RegattaResultTableEntryId: entry.id,
      },
    );

    if (result) {
      const discardedClass: string = result.Discarded === true ? 'bg-discarded text-gray-400' : '';

      return (
        <td
          className={`${this.showAllClass()} ${discardedClass}`}
          key={result.id}
        >
          <div className={'flex flex-row items-center justify-center'}>
            {this.renderResultColumns({
              race,
              result,
            })}
          </div>
        </td>
      );
    }

    return <td />;
  }

  render() {
    const {
      item,
    } = this.props;

    const publicRaces: Array<$FullRegattaResultTableRace> = _.filter(
      item.RegattaResultTableRaces,
      {
        Public: true,
      },
    );

    const extraColumnData = getExtraColumnData({
      regattaResultTable: item,
    });

    return (
      <div
        className={'RegattaResultTable overflow-y-auto'}
        key={item.id}
      >
        <table className={'table table_hoverable mx-auto'}>
          {this.renderHead({
            extraColumnData,
            publicRaces,
          })}
          {this.renderBody({
            extraColumnData,
            publicRaces,
          })}
        </table>
      </div>
    );
  }
}

export default RegattaResultTableView;
